import Button from "@material-ui/core/Button";
import { Dialog, DialogActions, DialogTitle, withStyles } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { getPerspective } from "../../utils/FPEUtils";
import { useSetRecoilState } from "recoil";
import { polarRotationState } from "../../store/PolarRotationState";
import { useATCPlan } from "../../store/ATCPlanState";
import { mapPerspectiveState } from "../../store/MapPerspectiveState";
import { getCycleDate } from "../../Authorization";
import { useAlert } from "../../hooks/useAlert";
import PlanEditor from "./PlanEditor";

const useStyles = makeStyles((theme) => ({
  btnContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: 300
  },
  btn: {
    margin: theme.spacing(1),
    '&:last-child': {
      marginLeft: 'auto'
    }
  },
  editorView: {
    height: "100%"
  }
}));

const EditorView = () => {
  const {atcPlan, changeATCPlan, clearATCPlan} = useATCPlan();
  const setPolarRotation = useSetRecoilState(polarRotationState);
  const setMapPerspective = useSetRecoilState(mapPerspectiveState);
  const [atcPlanText, setAtcPlanText] = useState(atcPlan.original.text);
  const {showSuccess, showError} = useAlert();
  const [revision, setRevision] = useState(0);
  const editorRef = useRef(null);

  const classes = useStyles();

  useEffect(() => {
    setAtcPlanText(atcPlan.edit.text);
  },[atcPlan.edit.text]);

  const changeATCPlanText = useCallback((planText) => {
    setAtcPlanText(planText);
  }, []);

  const parseATCPlan = useCallback(() => {
    if(atcPlanText.length === 0) {
      showError('ATC Plan is empty.', {timeout: 2000, close: false});
      return;
    }
    const params = new URLSearchParams();
    params.append('plan', atcPlanText);
    params.append('cycleDate', getCycleDate());
    axios.post('cgi/parser.cgi', params).then(res => {
      const perspective = getPerspective(res.data);
      setPolarRotation(perspective.centerLon);
      //先にPolarの基準経度を変更した上で再度perspectiveを取得
      setMapPerspective(getPerspective(res.data))
      changeATCPlan({text: atcPlanText, json: res.data});
      setRevision(revision + 1);
    }).catch(error => {
      showError('Failed to parse ATC', {timeout: 5000});
      console.log(`Failed to parse ATC : ${error.message} \n parameter : ${params.toString()}`);
    });
  }, [atcPlanText, setPolarRotation, setMapPerspective, changeATCPlan, showError, revision]);

  const newATCPlan = useCallback(() => {
    clearATCPlan();
    editorRef.current?.clear();
    setRevision(0);
  }, [clearATCPlan]);

  const copyATCPlan = () => {
    if(navigator.clipboard) {
      navigator.clipboard.writeText(atcPlanText).then(() => {
        showSuccess('copied to clipboard.', {timeout: 2000, close: false});
      });
    }
  }
  return (
    <div className={classes.editorView}>
      <div className={classes.btnContainer}>
        <Button variant="outlined" color="primary" size="small" className={classes.btn} onClick={parseATCPlan}>
          Apply
        </Button>
        <Button variant="outlined" color="secondary" size="small" className={classes.btn} onClick={copyATCPlan}>
          Copy
        </Button>
        <NewPlanButton onConfirm={newATCPlan}/>
      </div>
      <PlanEditor ref={editorRef} onChange={changeATCPlanText} revision={revision}/>
    </div>
  );
}

const NewPlanButton = ({onConfirm}) => {
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    setOpen(true);
  };

  const handleOk = () =>{
    setOpen(false);
    onConfirm()
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleKeyUp = e => {
    if (e.keyCode === 13) {
      //ENTER
      handleOk();
    }
  };

  const classes = useStyles();
  return (
    <>
      <Button variant="outlined" size="small" className={classes.btn} onClick={handleClick}>
        Clear
      </Button>
      <NewPlanDialog open={open} onClose={handleClose} onKeyUp={handleKeyUp}>
        <DialogTitle>Clear Plan?</DialogTitle>
        <DialogActions>
          <Button onClick={handleClose} color="primary">Cancel</Button>
          <Button onClick={handleOk} color="primary">Ok</Button>
        </DialogActions>
      </NewPlanDialog>
    </>
  );
};

const NewPlanDialog = withStyles(() => ({
  paper: {
    position: 'absolute',
    top: 135,
    left: 78,
    margin: 0
  }
}))(Dialog);

export default EditorView;